import { Box, Group, Text, Select, SelectProps, createStyles } from "@mantine/core";
import React, { forwardRef } from "react";
import { RpiLabelText } from "@rpi/openapi-core";
import { RpiAlert } from "@rpi/openapi-core";
import { IconV3 } from "@rpi/openapi-core";

const useStyles = createStyles((theme) => ({
  dropdown: {
    boxShadow: "0 4px 12px 1px rgba(0, 0, 0, 0.10)",
    border: "none",
  },
  rightSection: {
    pointerEvents: "none",
  },
  item: {
    "&[data-selected]": {
      backgroundColor: theme.other.semanticColors.bg.input,
      color: theme.other.semanticColors.textBold,
      fontWeight: theme.other.fontWeights.bold,
      "&:hover": {
        backgroundColor: theme.other.semanticColors.bg.inputHover,
      },
    },
  },
  header: {
    marginBottom: theme.spacing.xs,
  },
  optionalText: {
    fontSize: theme.fontSizes.sm,
    color: theme.other.semanticColors.textSubtle,
  },
  input: {
    backgroundColor: theme.other.semanticColors.bg.input,
    padding: theme.spacing.sm,
    color: theme.other.semanticColors.textBold,
    fontSize: theme.fontSizes.md,
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: theme.lineHeight,
    borderColor: theme.other.semanticColors.borderSubtle,
    "&::placeholder": {
      color: theme.other.semanticColors.textSubtle,
    },
    "&:hover": {
      background: theme.other.semanticColors.bg.inputHover,
    },
    "&:focus-visible": {
      borderColor: theme.other.semanticColors.borderSubtle,
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },
    "&:disabled": {
      backgroundColor: theme.other.semanticColors.bg.neutralDisabled,
      color: theme.other.semanticColors.text.disabled,
      opacity: "unset",
    },
    "&[data-error]": {
      borderColor: theme.other.semanticColors.alertDangerBorder,
    },
  },
  errorContainer: {
    display: "flex",
    gap: 4,
    alignItems: "start",
  },
  errorIcon: {
    lineHeight: "inherit",
  },
}));

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
  "data-selected"?: boolean;
}

const useItemStyles = createStyles(() => ({
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
}));

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(({ label, "data-selected": dataSelected, ...others }, ref) => {
  const { classes, theme } = useItemStyles();
  const isSelected = dataSelected || false;

  return (
    <div ref={ref} {...others}>
      <div className={classes.item}>
        <Text
          weight={isSelected ? theme.other.fontWeights.bold : undefined}
          color={isSelected ? theme.other.semanticColors.textBold : undefined}
        >
          {label}
        </Text>
        {isSelected && <IconV3 icon="check" color={theme.other.semanticColors.textBold} />}
      </div>
    </div>
  );
});

SelectItem.displayName = "SelectItem";

interface RpaDropdownProps extends SelectProps {
  optional?: boolean;
  errorMessage?: string;
  withinPortal?: boolean;
}

export const RpaDropdown: React.FC<RpaDropdownProps> = ({
  label,
  optional = false,
  error,
  withinPortal = false,
  ...props
}) => {
  const { classes, theme } = useStyles();

  return (
    <Box>
      <Group position="apart" className={classes.header}>
        <RpiLabelText>{label}</RpiLabelText>
        {optional && <Text className={classes.optionalText}>Optional</Text>}
      </Group>
      <Select
        radius={theme.radius.sm}
        size="md"
        classNames={{
          input: classes.input,
          dropdown: classes.dropdown,
          rightSection: classes.rightSection,
          item: classes.item,
        }}
        itemComponent={SelectItem}
        data-error={!!error || undefined}
        withinPortal={withinPortal}
        {...props}
        rightSection={<IconV3 icon="keyboard_arrow_down" />}
      />
      {error && <RpiAlert>{error}</RpiAlert>}
    </Box>
  );
};
